import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { 
  HomeIcon, 
  PencilIcon
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { NewsItem } from '../types/conference';

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  is_active: boolean;
  is_speaker: boolean;
  is_admin: boolean;
}

interface ScheduleItem {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  duration: number;
  room: string;
  track: string;
  type: string;
  speakers: Speaker[];
}

interface Speaker {
  id: string;
  name: string;
  title: string;
  bio: string;
  image: string;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  // News state
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [editingProfile, setEditingProfile] = useState<boolean>(false);
  
  // Schedule state
  const [allScheduleItems, setAllScheduleItems] = useState<ScheduleItem[]>([]);
  const [userSchedule, setUserSchedule] = useState<ScheduleItem[]>([]);
  const [loadingSchedule, setLoadingSchedule] = useState<boolean>(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  // Removed menu items as they're now integrated directly into the page

  useEffect(() => {
    const fetchNews = async () => {
      try {
        console.log('Fetching news from API...');
        setLoading(true);
        // Using the public news endpoint
        const response = await fetch(`${API_BASE_URL}/public/news`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch news');
        }
        
        const data = await response.json();
        console.log('Raw API response:', data);
        const rawNewsItems = data.news || [];
        console.log('Raw news items:', rawNewsItems);
        
        // Convert backend field names to frontend field names
        const formattedNewsItems: NewsItem[] = rawNewsItems.map((item: any): NewsItem => ({
          id: item.id?.toString?.() ?? String(item.id),
          title: item.title ?? '',
          content: item.content ?? '',
          publishDate: item.published_date ?? new Date().toISOString().split('T')[0],
          author: item.author || 'Conference Team',
          category: item.category || 'announcement',
          featured: Boolean(item.featured),
          status: item.status === 'draft' ? 'draft' : 'published'
        }));
        
        console.log('Formatted news items:', formattedNewsItems);
        // Only show published news items
        const publishedNews = formattedNewsItems.filter((item: NewsItem) => item.status === 'published');
        console.log('Published news items to display:', publishedNews);
        setNews(publishedNews);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Could not load news items. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);
  
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      try {
        setLoadingProfile(true);
        console.log('Setting up profile data...');
        
        // Use mock data for profile since the endpoint is not available
        // In a real app, we would fetch from the API endpoint
        const profileData = {
          id: String(user.id || '1'),
          full_name: user.full_name || 'John Doe',
          email: user.email || 'john.doe@example.com',
          is_active: user.is_active !== undefined ? user.is_active : true,
          is_speaker: user.is_speaker || false,
          is_admin: user.is_admin || false
        };
        
        // Log for debugging
        console.log('Using profile data:', profileData);
        
        setProfile(profileData);
      } catch (err) {
        console.error('Error setting up profile:', err);
        setProfileError('Could not load profile information. Please try again later.');
      } finally {
        setLoadingProfile(false);
      }
    };
    
    fetchProfile();
  }, [user]);
  
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setLoadingSchedule(true);
        console.log('Fetching schedule data...');
        
        // Fetch all schedule items from public endpoint
        const scheduleResponse = await fetch(`${API_BASE_URL}/public/schedule`);
        
        if (!scheduleResponse.ok) {
          throw new Error('Failed to fetch schedule');
        }
        
        const scheduleData = await scheduleResponse.json();
        console.log('Raw schedule response:', scheduleData);
        
        // Format the schedule items
        const formattedScheduleItems = (scheduleData.schedule || []).map((item: any) => ({
          id: item.id.toString(),
          title: item.title,
          description: item.description || '',
          date: new Date(item.date).toISOString().split('T')[0],
          time: item.time,
          duration: item.duration || 60,
          room: item.room || 'TBA',
          track: item.track || 'Main Track',
          type: item.type || 'talk',
          speakers: Array.isArray(item.speakers) ? item.speakers.map((speaker: any) => ({
            id: speaker.id.toString(),
            name: speaker.name,
            title: speaker.title || '',
            bio: speaker.bio || '',
            image: speaker.image || ''
          })) : []
        }));
        
        setAllScheduleItems(formattedScheduleItems);
        
        // In a real app, we would fetch the user's saved schedule from the backend
        // For now, we'll use localStorage to simulate saving the user's schedule
        const savedScheduleIds = localStorage.getItem('userSchedule') 
          ? JSON.parse(localStorage.getItem('userSchedule') || '[]')
          : [];
          
        if (savedScheduleIds.length > 0) {
          const userSavedSchedule = formattedScheduleItems.filter((item: ScheduleItem) => 
            savedScheduleIds.includes(item.id)
          );
          setUserSchedule(userSavedSchedule);
        }
      } catch (err) {
        console.error('Error fetching schedule:', err);
        setScheduleError('Could not load schedule information. Please try again later.');
      } finally {
        setLoadingSchedule(false);
      }
    };
    
    fetchSchedule();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-white pt-16">
      {/* Header */}
      <header className="bg-[#00529B] border-b border-[#00529B]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Welcome, {user?.full_name}</h1>
              <p className="mt-1 text-sm text-white/70">
                Stay up to date with conference news and updates
              </p>
            </div>
            <Link
              to="/"
              className="flex items-center text-white/80 hover:text-white transition-colors"
            >
              <HomeIcon className="h-5 w-5 mr-2" />
              <span>Back to Main Page</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Registration Banner */}
      <div className="bg-orange-50 border-b border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-orange-500 mr-3 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-orange-800">
                <p>
                  <strong>Important:</strong> Creating an account on this website is not the same as registering as an attendee.
                </p>
                <p className="mt-1">
                  To attend Language Intelligence 2025, you must register via Eventbrite.
                </p>
              </div>
            </div>
            <a
              href="https://www.eventbrite.co.uk/e/language-intelligence-2025-tickets-1299678576609"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors shadow-sm whitespace-nowrap"
            >
              Register on Eventbrite
              <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* News Section */}
        <div className="mb-8 mt-8">
          <h2 className="text-2xl font-bold text-[#00529B] mb-4">Conference News</h2>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-6 text-center">
                <p className="text-gray-600">Loading news...</p>
              </div>
            ) : error ? (
              <div className="p-6 text-center">
                <p className="text-red-500">{error}</p>
              </div>
            ) : news.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-600">No conference news available at this time.</p>
              </div>
            ) : (
              <div>
                {news.map((item) => (
                  <div key={item.id} className="border-b border-gray-200 last:border-b-0">
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-medium text-gray-900">{item.title}</h3>
                            {item.featured && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Featured
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {item.author && `By ${item.author}`} {item.publishDate && `| ${new Date(item.publishDate).toLocaleDateString()}`}
                          </p>
                        </div>
                        {item.category && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                          </span>
                        )}
                      </div>
                      <div className="mt-3 prose prose-sm max-w-none text-gray-600">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {item.content}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


        
        {/* Profile Section */}
        <div id="profile" className="mb-8">
          <h2 className="text-2xl font-bold text-[#00529B] mb-4">Your Profile</h2>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loadingProfile ? (
              <div className="p-6 text-center">
                <p className="text-gray-600">Loading profile...</p>
              </div>
            ) : profileError ? (
              <div className="p-6 text-center">
                <p className="text-red-500">{profileError}</p>
              </div>
            ) : profile ? (
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{profile.full_name}</h3>
                    <p className="mt-1 text-sm text-gray-500">{profile.email}</p>
                  </div>
                  <button 
                    onClick={() => setEditingProfile(!editingProfile)}
                    className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00529B]"
                  >
                    <PencilIcon className="-ml-0.5 mr-1 h-4 w-4" aria-hidden="true" />
                    {editingProfile ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                
                {editingProfile ? (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Full Name</label>
                      <input
                        type="text"
                        name="fullName"
                        id="fullName"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#00529B] focus:border-[#00529B] sm:text-sm"
                        defaultValue={profile.full_name}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#00529B] focus:border-[#00529B] sm:text-sm"
                        defaultValue={profile.email}
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            // Get the updated values from form fields
                            const nameField = document.getElementById('fullName') as HTMLInputElement;
                            const emailField = document.getElementById('email') as HTMLInputElement;
                            
                            if (!profile || !nameField || !emailField) return;
                            
                            // Create updated profile
                            const updatedProfile = {
                              ...profile,
                              full_name: nameField.value,
                              email: emailField.value
                            };
                            
                            // Log update information
                            console.log('Updating profile:', {
                              from: {
                                full_name: profile.full_name,
                                email: profile.email
                              },
                              to: {
                                full_name: updatedProfile.full_name,
                                email: updatedProfile.email
                              }
                            });
                            
                            // Call API to update profile
                            const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
                              method: 'PUT',  // Using the correct endpoint with /api prefix
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                'X-User-Role': 'user', // Required for authorization - must match backend expectations
                              },
                              body: JSON.stringify({
                                email: updatedProfile.email,
                                full_name: updatedProfile.full_name
                              })
                            });
                            
                            // Handle API error response
                            if (!response.ok) {
                              console.error('API error:', await response.text());
                              throw new Error(`Failed to update profile: ${response.status}`);
                            }
                            
                            // Get API response for confirmation
                            const data = await response.json();
                            console.log('Update response:', data);
                            
                            // Map the backend response to our frontend format
                            const updatedProfileFromServer = {
                              ...profile,
                              id: data.id || profile.id,
                              full_name: data.full_name || profile.full_name,
                              email: data.email || profile.email,
                              is_active: data.is_active !== undefined ? data.is_active : profile.is_active,
                              is_speaker: data.is_speaker !== undefined ? data.is_speaker : profile.is_speaker,
                              is_admin: data.is_admin !== undefined ? data.is_admin : profile.is_admin
                            };
                            
                            // Update local state with the merged profile from server
                            setProfile(updatedProfileFromServer);
                            setEditingProfile(false);
                          } catch (err) {
                            console.error('Error updating profile:', err);
                            alert('Could not update profile. However, your changes are visible in the UI for this session.');
                            
                            // Still update the UI for better UX even if the API fails
                            const nameField = document.getElementById('fullName') as HTMLInputElement;
                            const emailField = document.getElementById('email') as HTMLInputElement;
                            
                            if (profile && nameField && emailField) {
                              setProfile({
                                ...profile,
                                full_name: nameField.value,
                                email: emailField.value
                              });
                            }
                            
                            setEditingProfile(false);
                          }
                        }}
                        className="bg-[#00529B] text-white px-4 py-2 rounded hover:bg-[#00529B]/90"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Account Status</dt>
                        <dd className="mt-1 text-sm text-gray-900">{profile.is_active ? 'Active' : 'Inactive'}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Speaker Status</dt>
                        <dd className="mt-1 text-sm text-gray-900">{profile.is_speaker ? 'Speaker' : 'Attendee'}</dd>
                      </div>
                    </dl>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 text-center">
                <p className="text-gray-600">No profile information available.</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Schedule Section */}
        <div id="schedule" className="mt-12 mb-8">
          <h2 className="text-2xl font-bold text-[#00529B] mb-4">Your Conference Schedule</h2>
          <div className="mb-6">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Your Saved Sessions</h3>
                {loadingSchedule ? (
                  <div className="text-center">
                    <p className="text-gray-600">Loading your schedule...</p>
                  </div>
                ) : scheduleError ? (
                  <div className="text-center">
                    <p className="text-red-500">{scheduleError}</p>
                  </div>
                ) : userSchedule.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">You haven't saved any sessions yet.</p>
                    <p className="text-gray-500 mt-2">Browse available sessions below and add them to your schedule.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userSchedule.map((item) => (
                      <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{item.title}</h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {new Date(item.date).toLocaleDateString()} | {item.time} | {item.room}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {item.speakers.map(s => s.name).join(', ')}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              // Remove from user schedule
                              const updatedSchedule = userSchedule.filter(s => s.id !== item.id);
                              setUserSchedule(updatedSchedule);
                              
                              // Update localStorage
                              const savedIds = updatedSchedule.map(s => s.id);
                              localStorage.setItem('userSchedule', JSON.stringify(savedIds));
                            }}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00529B]"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Available Sessions</h3>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {loadingSchedule ? (
                <div className="p-6 text-center">
                  <p className="text-gray-600">Loading sessions...</p>
                </div>
              ) : scheduleError ? (
                <div className="p-6 text-center">
                  <p className="text-red-500">{scheduleError}</p>
                </div>
              ) : allScheduleItems.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-600">No sessions available at this time.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {allScheduleItems.map((item) => {
                    const isInUserSchedule = userSchedule.some(s => s.id === item.id);
                    return (
                      <div key={item.id} className="p-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center space-x-2">
                              <h3 className="text-lg font-medium text-gray-900">{item.title}</h3>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              {new Date(item.date).toLocaleDateString()} | {item.time} | Duration: {item.duration} min | Room: {item.room}
                            </p>
                            {item.speakers && item.speakers.length > 0 && (
                              <p className="mt-1 text-sm text-gray-600">
                                <strong>Speakers:</strong> {item.speakers.map(s => s.name).join(', ')}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              if (isInUserSchedule) {
                                // Remove from user schedule
                                const updatedSchedule = userSchedule.filter(s => s.id !== item.id);
                                setUserSchedule(updatedSchedule);
                                
                                // Update localStorage
                                const savedIds = updatedSchedule.map(s => s.id);
                                localStorage.setItem('userSchedule', JSON.stringify(savedIds));
                              } else {
                                // Add to user schedule
                                const updatedSchedule = [...userSchedule, item];
                                setUserSchedule(updatedSchedule);
                                
                                // Update localStorage
                                const savedIds = updatedSchedule.map(s => s.id);
                                localStorage.setItem('userSchedule', JSON.stringify(savedIds));
                              }
                            }}
                            className={`inline-flex items-center px-3 py-2 border ${isInUserSchedule ? 'border-red-300 text-red-700 hover:bg-red-50' : 'border-[#00529B] text-[#00529B] hover:bg-[#00529B]/5'} rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00529B]`}
                          >
                            {isInUserSchedule ? 'Remove from Schedule' : 'Add to Schedule'}
                          </button>
                        </div>
                        <div className="mt-3 prose prose-sm max-w-none text-gray-600">
                          {item.description}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
